'use client'

import React, { useState } from 'react'
import { ShoppingCart, X, ArrowRight } from 'lucide-react'
import { useRouter } from 'next/navigation'
import { useQuoteCart } from '@/lib/quote-cart-context'
import { Button } from './ui/button'
import { Badge } from './ui/badge'
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from './ui/sheet'

export function QuoteCartDropdown() {
  const router = useRouter()
  const [open, setOpen] = useState(false)
  const { quoteItems, getItemCount, getTotalQuantity, removeFromQuoteCart, clearQuoteCart } = useQuoteCart()
  const itemCount = getItemCount()

  const handleViewCart = () => {
    setOpen(false)
    router.push('/quote')
  }

  const handleClearCart = () => {
    clearQuoteCart()
  }

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <Button 
          variant="ghost" 
          size="icon" 
          className="relative hover:text-[#518BC4]"
          onClick={() => {
            console.log('Cart button clicked!')
            setOpen(true)
          }}
        >
          <ShoppingCart className="h-6 w-6" />
          {itemCount > 0 && (
            <Badge 
              className="absolute -top-1 -right-1 h-5 w-5 flex items-center justify-center p-0 text-xs"
              variant="default"
            >
              {itemCount}
            </Badge>
          )}
          <span className="sr-only">Quote Cart ({itemCount} items)</span>
        </Button>
      </SheetTrigger>
      <SheetContent side="right" className="w-full sm:max-w-lg p-0 flex flex-col">
        <SheetHeader className="p-4 border-b">
          <div className="flex items-center justify-between">
            <SheetTitle>Quote Request Cart</SheetTitle>
            {itemCount > 0 && (
              <Button
                variant="ghost"
                size="sm"
                onClick={handleClearCart}
                className="text-xs text-muted-foreground hover:text-destructive"
              >
                Clear All
              </Button>
            )}
          </div>
          {itemCount > 0 && (
            <p className="text-sm text-muted-foreground mt-1 text-left">
              {itemCount} item{itemCount !== 1 ? 's' : ''} · {getTotalQuantity()} total units
            </p>
          )}
        </SheetHeader>

        <div className="flex-1 overflow-y-auto">
          {itemCount === 0 ? (
            <div className="p-8 text-center">
              <ShoppingCart className="h-16 w-16 mx-auto text-muted-foreground/50 mb-4" />
              <p className="text-sm text-muted-foreground mb-1">Your quote cart is empty</p>
              <p className="text-xs text-muted-foreground">
                Click "Request Quote" on products to add them here
              </p>
            </div>
          ) : (
            <div className="divide-y">
              {quoteItems.map((item) => (
                <div key={item.product.id} className="p-4 hover:bg-muted/50 transition-colors">
                  <div className="flex gap-3">
                    <div className="relative w-16 h-16 bg-white rounded border flex-shrink-0 overflow-hidden">
                      <img
                        src={item.product.images?.[0] || '/placeholder.jpg'}
                        alt={item.product.title}
                        className="w-full h-full object-contain"
                      />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h4 className="font-medium text-sm mb-0.5">
                        {item.product.title}
                      </h4>
                      <p className="text-xs text-muted-foreground mb-1">
                        {item.product.partNumber}
                      </p>
                      <div className="flex items-center gap-2">
                        <span className="text-xs font-medium">
                          Qty: {item.quantity}
                        </span>
                        {item.product.manufacturer && (
                          <span className="text-xs text-muted-foreground">
                            {item.product.manufacturer}
                          </span>
                        )}
                      </div>
                      {item.notes && (
                        <p className="text-xs text-muted-foreground mt-1 italic">
                          Note: {item.notes}
                        </p>
                      )}
                    </div>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 flex-shrink-0"
                      onClick={() => removeFromQuoteCart(item.product.id)}
                    >
                      <X className="h-4 w-4" />
                      <span className="sr-only">Remove {item.product.title}</span>
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {itemCount > 0 && (
          <div className="p-4 border-t bg-muted/30 mt-auto">
            <Button
              onClick={handleViewCart}
              className="w-full"
              size="lg"
            >
              Continue to Quote Request
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </div>
        )}
      </SheetContent>
    </Sheet>
  )
}
